/**
 * Frame + corner geometry for the Bezel component.
 *
 * Every path-producing helper walks the same four corners through one
 * `CornerSpec` model, so beveled (straight chamfer) and rounded (arc) corners
 * share a single code path — adding a shape never means editing two functions.
 * `radius` wins over `bevel` when both are set; `0/0` yields a square corner.
 */

export type Corner = 'tl' | 'tr' | 'br' | 'bl';

/** A value applied to all four corners, or per-corner as [tl, tr, br, bl]. */
export type CornerQuad = number | [number, number, number, number];

/** Resolved per-corner geometry: chamfer size and arc radius (px). */
export type CornerSpec = { bevel: number; radius: number };

/** Clockwise traversal order, also used to key per-corner records. */
export const CORNER_ORDER: readonly Corner[] = ['tl', 'tr', 'br', 'bl'] as const;

/** Normalise a quad shorthand to an explicit [tl, tr, br, bl] tuple. */
export function resolveQuad(value: CornerQuad): [number, number, number, number] {
  return typeof value === 'number' ? [value, value, value, value] : value;
}

/** Combine bevel + radius quads into a per-corner spec record. */
export function cornerSpecs(bevel: CornerQuad, radius: CornerQuad): Record<Corner, CornerSpec> {
  const b = resolveQuad(bevel);
  const r = resolveQuad(radius);
  return {
    tl: { bevel: b[0], radius: r[0] },
    tr: { bevel: b[1], radius: r[1] },
    br: { bevel: b[2], radius: r[2] },
    bl: { bevel: b[3], radius: r[3] },
  };
}

/** Effective cut for a corner: radius (arc) wins over bevel (chamfer); inset shrinks it. */
function cornerCut(spec: CornerSpec, inset: number): { k: number; round: boolean } {
  const round = spec.radius > 0;
  const base = round ? spec.radius : spec.bevel;
  return { k: Math.max(0, base - inset / 2), round };
}

/** One corner turn: arc when rounded, line when beveled, nothing when square. */
function turn(round: boolean, k: number, x: number, y: number): string {
  if (k <= 0) {
    return '';
  }
  return round ? `A ${k} ${k} 0 0 1 ${x} ${y}` : `L ${x} ${y}`;
}

/**
 * Main frame outline (beveled/rounded rectangle) for CSS `clip-path`.
 * When `inset > 0` the path is centered within the original bounds — used to
 * derive the inner edge of the stroke band.
 */
export function framePath(width: number, height: number, corners: Record<Corner, CornerSpec>, inset = 0): string {
  const o = inset / 2;
  const x0 = o;
  const y0 = o;
  const x1 = width - o;
  const y1 = height - o;

  const tl = cornerCut(corners.tl, inset);
  const tr = cornerCut(corners.tr, inset);
  const br = cornerCut(corners.br, inset);
  const bl = cornerCut(corners.bl, inset);

  // Clockwise from the top edge, just past the top-left corner.
  return [
    `M ${x0 + tl.k} ${y0}`,
    `L ${x1 - tr.k} ${y0}`,
    turn(tr.round, tr.k, x1, y0 + tr.k),
    `L ${x1} ${y1 - br.k}`,
    turn(br.round, br.k, x1 - br.k, y1),
    `L ${x0 + bl.k} ${y1}`,
    turn(bl.round, bl.k, x0, y1 - bl.k),
    `L ${x0} ${y0 + tl.k}`,
    turn(tl.round, tl.k, x0 + tl.k, y0),
    'Z',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Frame band for evenodd fills: a `bandWidth`-thick ring whose outer edge sits
 * `spread` px inside the frame edge (0 = flush). Stays within the frame box so
 * it clips cleanly; the outward glow halo comes from blurring the result.
 * Generalises the stroke band — the stroke is just `spread = 0`.
 */
export function bandPath(
  width: number,
  height: number,
  corners: Record<Corner, CornerSpec>,
  bandWidth: number,
  spread = 0,
): string {
  const outer = framePath(width, height, corners, spread * 2);
  const inner = framePath(width, height, corners, (spread + bandWidth) * 2);
  return `${outer} ${inner}`;
}

/**
 * Stroke-only path: the frame band at `strokeWidth` thickness, flush to the
 * edge, drawn with the evenodd rule so the band becomes the visible stroke.
 */
export function strokePath(
  width: number,
  height: number,
  corners: Record<Corner, CornerSpec>,
  strokeWidth: number,
): string {
  return bandPath(width, height, corners, strokeWidth);
}

/** Per-corner basis: rect-vertex origin + edge directions (sa along x, sb along y). */
const CORNER_BASIS: Record<Corner, (w: number, h: number) => [number, number, number, number]> = {
  tl: () => [0, 0, 1, 1],
  tr: (w) => [w, 0, -1, 1],
  br: (w, h) => [w, h, -1, -1],
  bl: (_w, h) => [0, h, 1, -1],
};

/**
 * L-shaped corner bracket hugging one corner, for CSS `clip-path`.
 *
 * Computed in a corner-local basis (origin at the rect vertex, `a` along the
 * first edge, `b` along the second) so all four corners — and both chamfer and
 * arc shapes — come from a single parametrisation. `legMul` shortens the second
 * leg relative to the first.
 */
export function cornerBracketPath(
  corner: Corner,
  width: number,
  height: number,
  spec: CornerSpec,
  thickness: number,
  legLength: number,
  legMul = 0.25,
): string {
  const { k, round } = cornerCut(spec, 0);
  const t = thickness;
  const l = legLength;
  const [ox, oy, sa, sb] = CORNER_BASIS[corner](width, height);
  const p = (a: number, b: number): string => `${ox + a * sa} ${oy + b * sb}`;

  // Mirroring flips arc handedness; even-parity corners (tl, br) keep it.
  const even = sa * sb > 0;
  const arc = round && k > 0;
  const ir = Math.max(0, k - t);

  const outerCorner = arc ? `A ${k} ${k} 0 0 ${even ? 1 : 0} ${p(k, 0)}` : `L ${p(k, 0)}`;
  const innerCorner = arc
    ? `L ${p(k, t)} A ${ir} ${ir} 0 0 ${even ? 0 : 1} ${p(t, k)}`
    : `L ${p(k + t * 0.7, t)} L ${p(t, k + t * 0.7)}`;

  return [
    `M ${p(0, k + l * legMul)}`,
    `L ${p(0, k)}`,
    outerCorner,
    `L ${p(k + l, 0)}`,
    `L ${p(k + l, t)}`,
    innerCorner,
    `L ${p(t, k + l * legMul)}`,
    'Z',
  ].join(' ');
}
